import {
  BaseClass,
  EdgePortCandidates,
  GenericLayoutData,
  HierarchicalLayout,
  HierarchicalLayoutContext,
  HierarchicalLayoutData,
  HierarchicalLayoutLayer,
  HierarchicalLayoutLayerType,
  HierarchicalLayoutNodeType,
  HierarchicalLayoutRoutingStyle,
  ICoordinateAssigner,
  IDrawingDistanceCalculator,
  IMapper,
  Interval,
  LayoutEdge,
  LayoutEdgeLabel,
  LayoutGraph,
  LayoutGraphAlgorithms,
  LayoutGraphGrouping,
  LayoutGraphItem,
  LayoutNode,
  LayoutNodeLabel,
  LayoutStageBase,
  ListCell,
  NodeDataKey
} from '@yfiles/yfiles';
import { FlowEdgeThroughNodeStage } from './FlowEdgeThroughNodeStage';
import {
  CompactOctilinearRoutingStage,
  NonHorizontalSegmentMovement
} from './CompactOctilinearRoutingStage.ts';

/**
 * A custom layout stage that enhances the hierarchical layout with
 * improved edge routing for value stream diagrams.
 *
 * This class implements advanced edge deconfliction techniques:
 * - Edge grouping by product
 * - Fixed port candidates
 * - Critical/alignment edges for better node alignment
 * - Custom sequencing for layers
 */
export default class ValueStreamLayout extends LayoutStageBase {
  constructor(coreLayout: HierarchicalLayout) {
    super(coreLayout);
  }

  protected applyLayoutImpl(graph: LayoutGraph): void {
    const hl = this.coreLayout as HierarchicalLayout;
    const context = graph.context;

    const layoutData = hl.createLayoutData(graph);

    context.pushLayer(layoutData);
    hl.applyLayout(graph);
    context.popLayer();

    const layerIds = layoutData.layerIndicesResult;

    graph.edges.forEach((edge) => {
      if (edge.tag.type !== 'INTER_STAGE_FLOW' && edge.tag.type !== 'PRODUCT_TO_LEAF') {
        return;
      }

      layoutData.sourceGroupIds.mapper.set(edge, `s:${edge.tag.product}-${edge.source.tag.stage}`);
      layoutData.targetGroupIds.mapper.set(edge, `t:${edge.tag.product}-${edge.target.tag.stage}`);
    });

    // Set fixed port candidates for edges
    for (const edge of graph.edges) {
      layoutData.ports.sourcePortCandidates.mapper.set(
        edge,
        new EdgePortCandidates().addFixedCandidate('right', edge.sourcePortOffset)
      );
      layoutData.ports.targetPortCandidates.mapper.set(
        edge,
        new EdgePortCandidates().addFixedCandidate('left', edge.targetPortOffset)
      );
    }

    const maxLayer = Math.max(...graph.nodes.map((node) => layerIds.get(node) ?? 0));
    const sequenceIds = layoutData.sequenceIndicesResult;
    const nodesGroupedByLayer = this.groupNodesByLayer(graph, layerIds, sequenceIds, maxLayer);
    const nodesToAlign = this.calculateNodesToAlign(graph, nodesGroupedByLayer);
    const additionalLayoutData = this.alignNodes(nodesToAlign, layerIds, graph);
    this.addSequenceConstraints(maxLayer, graph, layerIds, nodesGroupedByLayer, layoutData);

    // Fix layer indices for nodes
    for (const node of graph.nodes) {
      if (graph.isGroupNode(node)) {
        continue;
      }
      layoutData.givenLayersIndices.mapper.set(node, layerIds.get(node));
    }
    hl.fromScratchLayeringStrategy = 'user-defined';

    const flowEdgeStage = hl.layoutStages.get(FlowEdgeThroughNodeStage);
    if (flowEdgeStage) {
      // The port candidates are already placed at the correct positions, and the added edges to enforce the alignment
      // may cause the stage to place too many edges at the vertical center of the nodes
      flowEdgeStage.enabled = false;
    }

    const coreCoordinateAssigner = hl.coordinateAssigner;
    hl.core.coordinateAssigner = new ValueStreamCoordinateAssigner(coreCoordinateAssigner);

    const addOctilinearRoutingStage =
      hl.defaultEdgeDescriptor.routingStyleDescriptor.defaultRoutingStyle ===
      HierarchicalLayoutRoutingStyle.OCTILINEAR;
    let octilinearRoutingStage: CompactOctilinearRoutingStage | null = null;
    if (addOctilinearRoutingStage) {
      octilinearRoutingStage = new CompactOctilinearRoutingStage();
      octilinearRoutingStage.edgeDistance = hl.edgeDistance;
      octilinearRoutingStage.desiredOctilinearSegmentLength =
        hl.defaultEdgeDescriptor.minimumOctilinearSegmentLength;
      hl.layoutStages.append(octilinearRoutingStage);

      const movementMapping = additionalLayoutData.addItemMapping(
        CompactOctilinearRoutingStage.NON_HORIZONTAL_SEGMENT_MOVEMENT_KEY
      );
      movementMapping.mapperFunction = (e: LayoutEdge) =>
        e.tag?.type === 'PRODUCT_TO_LEAF'
          ? NonHorizontalSegmentMovement.ALWAYS_BOTTOM
          : NonHorizontalSegmentMovement.AUTOMATIC;
    }

    context.pushLayer(layoutData.combineWith(additionalLayoutData));
    hl.applyLayout(graph);
    context.popLayer();

    // Reset the layout configuration
    hl.core.coordinateAssigner = coreCoordinateAssigner;
    if (addOctilinearRoutingStage) {
      hl.layoutStages.remove(octilinearRoutingStage!);
    }
  }

  /**
   * Groups nodes by their layer for further processing
   */
  private groupNodesByLayer(
    graph: LayoutGraph,
    layerIds: IMapper<LayoutNode, number>,
    sequenceIds: IMapper<LayoutNode, number>,
    maxLayer: number
  ): LayoutNode[][] {
    const result: LayoutNode[][] = Array.from({ length: maxLayer + 1 }, () => []);

    for (const node of graph.nodes) {
      if (graph.isGroupNode(node)) {
        continue;
      }
      const layer = layerIds.get(node);
      if (layer !== undefined && layer !== null) {
        result[layer].push(node);
      }
    }

    // Sort nodes in each layer by sequence ID
    for (const layer of result) {
      layer.sort((a, b) => {
        const seqA = sequenceIds.get(a) ?? 0;
        const seqB = sequenceIds.get(b) ?? 0;
        return seqA - seqB;
      });
    }

    return result;
  }

  /**
   * Finds long edges that span multiple layers and groups them by layer ignoring edges that are
   * marked as 'aligned'.
   */
  private longEdgesPerLayer(
    maxLayer: number,
    graph: LayoutGraph,
    layerIds: IMapper<LayoutNode, number>
  ): LayoutEdge[][] {
    const result: LayoutEdge[][] = Array.from({ length: maxLayer + 1 }, () => []);

    for (const edge of graph.edges) {
      if (edge.tag === 'aligned') {
        // ignore the edges that were inserted to align the nodes
        continue;
      }
      const sourceLayer = layerIds.get(edge.source) ?? 0;
      const targetLayer = layerIds.get(edge.target) ?? 0;
      if (targetLayer - sourceLayer > 1) {
        for (let i = sourceLayer + 1; i < targetLayer; i++) {
          result[i].push(edge);
        }
      }
    }

    return result;
  }

  /**
   * Identifies nodes that should be aligned vertically across layers.
   *
   * We ensure that groups that span multiple layers appear as one block.
   * For example, suppose that there is a group with two nodes in two different layers.
   * If the node in the first layer is at position 7 and a node in the second layer at position 3,
   * then the node in the first layer needs to be shifted to position 7, too, and all nodes below
   * in the first layer need to be shifted accordingly.
   */
  private calculateNodesToAlign(graph: LayoutGraph, nodesGroupedByLayer: LayoutNode[][]) {
    const dependencyGraph = new LayoutGraph();
    const nodeToNodeInDependencyGraph = new Map<LayoutNode, LayoutNode>();

    for (const layer of nodesGroupedByLayer) {
      let prev: LayoutNode | null = null;
      for (const node of layer) {
        const current = dependencyGraph.createNode();
        current.tag = node;
        current.layout.x = node.layout.x;
        current.layout.y = node.layout.y;
        current.layout.width = node.layout.width;
        current.layout.height = node.layout.height;
        nodeToNodeInDependencyGraph.set(node, current);
        if (prev) {
          // the nodes are ordered from bottom to top, but we want the dependency edges to go from top to bottom
          dependencyGraph.createEdge(current, prev);
        }
        prev = current;
      }
    }

    const grouping = LayoutGraphGrouping.createReadOnlyView(graph);
    const outerGroup = grouping.getChildren(null).first();
    const groups = grouping.getDescendants(outerGroup).filter((n) => grouping.isGroupNode(n));

    for (const group of groups) {
      const predecessors = [] as LayoutNode[];
      const descendants = new Set(grouping.getDescendants(group));

      for (const node of grouping.getDescendants(group)) {
        if (grouping.isGroupNode(node)) {
          continue;
        }
        const dependencyNode = nodeToNodeInDependencyGraph.get(node)!;
        if (dependencyNode.inDegree > 0) {
          const predecessorDependencyNode = dependencyNode.inEdges.first()!.source;
          const predecessor = predecessorDependencyNode.tag as LayoutNode;
          if (!descendants.has(predecessor)) {
            predecessors.push(predecessorDependencyNode);
          }
        }
      }

      for (const node of grouping.getDescendants(group)) {
        if (grouping.isGroupNode(node)) {
          continue;
        }
        const dependencyNode = nodeToNodeInDependencyGraph.get(node)!;
        for (const predecessor of predecessors) {
          dependencyGraph.createEdge(predecessor, dependencyNode);
        }
      }
    }

    const adjustedSequenceIds = new Map<LayoutNode, number>();
    for (const node of LayoutGraphAlgorithms.topologicalNodeOrder(dependencyGraph)) {
      const maxPredecessorId = Math.max(
        -1,
        Math.max(
          ...node.inEdges.map((edge) => adjustedSequenceIds.get(edge.source.tag as LayoutNode)!)
        )
      );

      adjustedSequenceIds.set(node.tag as LayoutNode, maxPredecessorId + 1);
    }

    const maxSequenceId = Math.max(...adjustedSequenceIds.values());

    const nodesToAlign = Array.from({ length: maxSequenceId + 1 }, () => [] as LayoutNode[]);

    for (const node of graph.nodes) {
      if (graph.isGroupNode(node)) {
        continue;
      }
      const id = adjustedSequenceIds.get(node)!;
      nodesToAlign[id].push(node);
    }

    return nodesToAlign;
  }

  private alignNodes(
    nodesToAlign: LayoutNode[][],
    layerIds: IMapper<LayoutNode, number>,
    graph: LayoutGraph
  ): GenericLayoutData<LayoutNode, LayoutEdge, LayoutNodeLabel, LayoutEdgeLabel> {
    const alignmentKey = new NodeDataKey(
      'yWorks.Layout.Hierarchical.CoordinateAssigner.NodeToAlignWithDataKey'
    );
    const layoutData = new GenericLayoutData<
      LayoutNode,
      LayoutEdge,
      LayoutNodeLabel,
      LayoutEdgeLabel
    >();
    const alignmentMapper = new Map<LayoutNode, LayoutNode>();
    graph.context.addItemData(alignmentKey, IMapper.from(alignmentMapper));

    for (let i = 0; i < nodesToAlign.length; i++) {
      const nodes = nodesToAlign[i];
      nodes.sort((a, b) => layerIds.get(a)! - layerIds.get(b)!);
      let prev: LayoutNode | null = null;
      for (const node of nodes) {
        if (node.inDegree === 0) {
          // ignore the headers
          continue;
        }
        if (prev) {
          alignmentMapper.set(node, prev);
        }
        prev = node;
      }
    }
    return layoutData;
  }

  /**
   * Adds sequence constraints to control the order of nodes and edges
   */
  private addSequenceConstraints(
    maxLayer: number,
    graph: LayoutGraph,
    layerIds: IMapper<LayoutNode, number>,
    nodesGroupedByLayer: LayoutNode[][],
    layoutData: HierarchicalLayoutData<
      LayoutNode,
      LayoutEdge,
      LayoutGraphItem,
      LayoutNodeLabel,
      LayoutEdgeLabel
    >
  ) {
    const layerToLongEdges = this.longEdgesPerLayer(maxLayer, graph, layerIds);

    for (let i = 0; i < nodesGroupedByLayer.length; i++) {
      const nodes = nodesGroupedByLayer[i];
      let prev: LayoutNode | null = null;
      for (const node of nodes) {
        if (prev) {
          layoutData.sequenceConstraints.placeNodeBeforeNode(prev, node);
        }
        prev = node;
      }

      const edges = layerToLongEdges[i];

      for (const edge of edges) {
        layoutData.sequenceConstraints.placeEdgeBeforeNode(edge, nodes[0]);
      }
    }
  }
}

/**
 * Improves the edge routing by moving some bends and source/target group nodes to better locations.
 */
class ValueStreamCoordinateAssigner extends BaseClass(ICoordinateAssigner) {
  public constructor(public coreAssigner: ICoordinateAssigner) {
    super();
  }

  public assignSequenceCoordinates(
    graph: LayoutGraph,
    layoutContext: HierarchicalLayoutContext,
    drawingDistanceCalculator: IDrawingDistanceCalculator | null
  ): void {
    this.coreAssigner.assignSequenceCoordinates(graph, layoutContext, drawingDistanceCalculator);
    this.moveBendsAtLayerStart(graph, layoutContext, drawingDistanceCalculator!);
    this.ensureNoDistanceViolations(graph, layoutContext, drawingDistanceCalculator!);
    this.moveBendsBeforeTargetEdgeGroup(graph, layoutContext);
    this.moveEdgeGroupNodes(layoutContext);
    this.ensureNoDistanceViolations(graph, layoutContext, drawingDistanceCalculator!);
  }

  public assignLayerCoordinates(
    graph: LayoutGraph,
    layoutContext: HierarchicalLayoutContext
  ): void {
    this.coreAssigner.assignLayerCoordinates(graph, layoutContext);
  }

  private moveBendsAtLayerStart(
    graph: LayoutGraph,
    layoutContext: HierarchicalLayoutContext,
    drawingDistanceCalculator: IDrawingDistanceCalculator
  ): void {
    for (const layer of layoutContext.layers) {
      const blockerCell = layer.nodes.findCell(
        (node) => layoutContext.getNodeContext(node)?.type !== HierarchicalLayoutNodeType.BEND
      );
      if (blockerCell === null) {
        continue;
      }

      // We want to ignore the first group begin as this group will be hidden in the resulting layout
      let rightCell: ListCell<LayoutNode>;
      if (
        layoutContext.getNodeContext(blockerCell.info)?.type ===
        HierarchicalLayoutNodeType.GROUP_BEGIN
      ) {
        rightCell = blockerCell.next!;
      } else {
        rightCell = blockerCell;
      }
      let leftCell = blockerCell.previous;

      while (leftCell) {
        const right = rightCell.info;
        const left = leftCell.info;

        const distance = drawingDistanceCalculator.getMinimumDistance(
          graph,
          layer,
          layoutContext,
          left,
          right
        );
        left.layout.x = right.layout.x - distance - left.layout.width;

        rightCell = leftCell;
        leftCell = rightCell.previous;
      }
    }
  }

  private moveBendsBeforeTargetEdgeGroup(
    graph: LayoutGraph,
    layoutContext: HierarchicalLayoutContext
  ): void {
    for (const node of graph.nodes) {
      if (layoutContext.getNodeContext(node)?.type !== HierarchicalLayoutNodeType.BEND) {
        continue;
      }
      const predecessor = node.inEdges.first()!.source;
      const successor = node.outEdges.first()!.target;
      if (
        layoutContext.getNodeContext(predecessor)?.type !== HierarchicalLayoutNodeType.BEND ||
        layoutContext.getNodeContext(successor)?.type !==
          HierarchicalLayoutNodeType.TARGET_GROUP_NODE
      ) {
        continue;
      }
      node.layout.x = predecessor.layout.x;
    }
  }

  private moveEdgeGroupNodes(layoutContext: HierarchicalLayoutContext) {
    for (const layer of layoutContext.layers) {
      if (layer.type === HierarchicalLayoutLayerType.SOURCE_GROUP_NODES) {
        for (const node of layer.nodes) {
          const sourceType = layoutContext.getNodeContext(node)?.type;
          if (
            (sourceType !== HierarchicalLayoutNodeType.SOURCE_GROUP_NODE &&
              sourceType != HierarchicalLayoutNodeType.BEND) ||
            node.outDegree !== 1
          ) {
            continue;
          }

          const target = node.outEdges.first()!.target;
          const targetType = layoutContext.getNodeContext(target)?.type;

          const sourceInterval =
            sourceType === HierarchicalLayoutNodeType.SOURCE_GROUP_NODE
              ? this.calculateSourceGroupInterval(node)
              : new Interval(node.layout.centerX, node.layout.centerX);
          const targetInterval =
            targetType === HierarchicalLayoutNodeType.TARGET_GROUP_NODE
              ? this.calculateTargetGroupInterval(target)
              : new Interval(target.layout.centerX, target.layout.centerX);

          if (sourceInterval.intersects(targetInterval)) {
            const pos =
              (Math.max(sourceInterval.minimum, targetInterval.minimum) +
                Math.min(sourceInterval.maximum, targetInterval.maximum)) *
              0.5;
            node.layout.centerX = pos;
            target.layout.centerX = pos;
          } else if (sourceInterval.maximum <= targetInterval.minimum) {
            const pos = targetInterval.minimum;
            node.layout.centerX = pos;
            target.layout.centerX = pos;
          } else {
            const pos = sourceInterval.minimum;
            node.layout.centerX = pos;
            target.layout.centerX = pos;
          }
        }
      }
    }
  }

  private calculateSourceGroupInterval(node: LayoutNode): Interval {
    const min = Math.min(...node.inEdges.map((edge) => edge.sourcePortLocation.x));
    const max = Math.max(...node.inEdges.map((edge) => edge.sourcePortLocation.x));
    return new Interval(min, max);
  }

  private calculateTargetGroupInterval(node: LayoutNode): Interval {
    const min = Math.min(...node.outEdges.map((edge) => edge.targetPortLocation.x));
    const max = Math.max(...node.outEdges.map((edge) => edge.targetPortLocation.x));
    return new Interval(min, max);
  }

  private ensureNoDistanceViolations(
    graph: LayoutGraph,
    layoutContext: HierarchicalLayoutContext,
    drawingDistanceCalculator: IDrawingDistanceCalculator
  ): void {
    for (const layer of layoutContext.layers) {
      // Work from back to front since we have space below the layout, but the top should remain aligned.
      // We ignore the outer group in the layer as it will be invisible.
      // Moreover, we ignore the group begin and end nodes on the first layer of the group as the group has been
      // extended to one layer above the actual start of the group.

      const outerGroupBegin = layer.nodes.find(
        (n) => layoutContext.getNodeContext(n)?.type === HierarchicalLayoutNodeType.GROUP_BEGIN
      );
      const outerGroupEnd = layer.nodes.findLast(
        (n) => layoutContext.getNodeContext(n)?.type === HierarchicalLayoutNodeType.GROUP_END
      );

      const isNodeRelevant = (node: LayoutNode): boolean => {
        const type = layoutContext.getNodeContext(node)?.type;
        if (
          type !== HierarchicalLayoutNodeType.GROUP_BEGIN &&
          type !== HierarchicalLayoutNodeType.GROUP_END
        ) {
          return true;
        }
        if (node === outerGroupBegin || node === outerGroupEnd) {
          return false;
        }
        return node.inDegree === 1;
      };

      const nodesInLayer = layer.nodes.filter((n) => isNodeRelevant(n)).toArray();
      nodesInLayer.sort((n1, n2) => n2.layout.centerX - n1.layout.centerX);

      for (let i = 0; i < nodesInLayer.length - 1; i++) {
        const right = nodesInLayer[i];
        const left = nodesInLayer[i + 1];
        const currentDistance = right.layout.x - left.layout.x - left.layout.width;
        const minimumDistance = this.getMinimumDistance(
          graph,
          layoutContext,
          layer,
          drawingDistanceCalculator,
          left,
          right
        );
        if (minimumDistance > currentDistance) {
          left.layout.x -= minimumDistance - currentDistance;
        }
      }
    }
  }

  private getMinimumDistance(
    graph: LayoutGraph,
    layoutContext: HierarchicalLayoutContext,
    layer: HierarchicalLayoutLayer,
    drawingDistanceCalculator: IDrawingDistanceCalculator,
    left: LayoutNode,
    right: LayoutNode
  ): number {
    const defaultDistance = drawingDistanceCalculator.getMinimumDistance(
      graph,
      layer,
      layoutContext,
      left,
      right
    );
    const leftCorrection = this.calculateDistanceCorrectionForNode(left, layoutContext, true);
    const rightCorrection = this.calculateDistanceCorrectionForNode(right, layoutContext, false);
    return defaultDistance - leftCorrection - rightCorrection;
  }

  private calculateDistanceCorrectionForNode(
    node: LayoutNode,
    layoutContext: HierarchicalLayoutContext,
    isLeftNode: boolean
  ): number {
    const type = layoutContext.getNodeContext(node)?.type;
    if (
      type === HierarchicalLayoutNodeType.SOURCE_GROUP_NODE ||
      type === HierarchicalLayoutNodeType.TARGET_GROUP_NODE
    ) {
      const thickness = Math.max(
        ...node.edges.map((edge) => layoutContext.getEdgeContext(edge)?.thickness ?? 0)
      );
      const width = node.layout.width;
      return 0.5 * thickness + 0.5 * width;
    }
    if (type === HierarchicalLayoutNodeType.GROUP_BEGIN) {
      return isLeftNode ? 0.5 * node.layout.width : 0;
    }
    if (type === HierarchicalLayoutNodeType.GROUP_END) {
      return isLeftNode ? 0 : 0.5 * node.layout.width;
    }
    return 0;
  }
}
